import {
  makeStyles,
  mergeClasses,
  Popover,
  PopoverSurface,
  PopoverTrigger,
  ToolbarButton,
  Tooltip,
} from "@fluentui/react-components";
import {
  BookTemplate20Regular,
  BrainCircuitRegular,
  DocumentMultiple24Regular,
  Drafts24Regular,
  Image24Regular,
  Navigation20Regular,
  Settings24Regular,
} from "@fluentui/react-icons";
import React, { useEffect, useState } from "react";

const useStyles = makeStyles({
  navigationSurface: {
    width: "250px",
    display: "flex",
    flexDirection: "column",
    padding: "10px",
  },
  menuButton: {
    marginBottom: "5px",
    justifyContent: "flex-start",
  },
  activeButton: {
    color: "#0078d4",
    backgroundColor: "rgba(0, 120, 212, 0.1)",
  },
});

interface NavigationButton {
  key: string;
  icon: React.JSX.Element;
  tooltip: string;
}

interface AdaptiveIconsListProps {
  onScreenChange: (screen: string) => void;
  currentScreen: string;
}

const screenButtons: NavigationButton[] = [
  {
    key: "chat",
    icon: <BrainCircuitRegular />,
    tooltip: "AI Assistant",
  },
  {
    key: "documents",
    icon: <DocumentMultiple24Regular />,
    tooltip: "Documents",
  },
  {
    key: "diagrams",
    icon: <Image24Regular />,
    tooltip: "Diagrams",
  },
  {
    key: "policies",
    icon: <BookTemplate20Regular />,
    tooltip: "Policies",
  },
  {
    key: "custom-prompts",
    icon: <Drafts24Regular />,
    tooltip: "Prompt Libary",
  },
  {
    key: "settings",
    icon: <Settings24Regular />,
    tooltip: "Settings",
  },
];

export const AdaptiveIconsList: React.FC<AdaptiveIconsListProps> = ({ onScreenChange, currentScreen }) => {
  const styles = useStyles();
  const [openPopover, setOpenPopover] = useState(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleScreenChange = (screen: string) => {
    setOpenPopover(false);
    onScreenChange(screen);
  };

  if (windowWidth >= 485) {
    return (
      <>
        {screenButtons.map(({ key, icon, tooltip }) => {
          const isActive = key === currentScreen;

          return (
            <Tooltip key={key} withArrow content={tooltip} relationship="label">
              <ToolbarButton
                appearance="subtle"
                icon={icon}
                onClick={() => onScreenChange(key)}
                className={isActive ? styles.activeButton : ""}
              />
            </Tooltip>
          );
        })}
      </>
    );
  }

  return (
    <Popover open={openPopover} onOpenChange={(_, data) => setOpenPopover(data.open)}>
      <PopoverTrigger>
        <Tooltip key={"navigationMenu"} withArrow content={"Navigation Menu"} relationship="label">
          <ToolbarButton
            aria-label="Navigation Menu"
            icon={<Navigation20Regular />}
            onClick={() => setOpenPopover(!openPopover)}
          />
        </Tooltip>
      </PopoverTrigger>
      <PopoverSurface className={styles.navigationSurface}>
        {screenButtons.map(({ key, icon, tooltip }) => {
          const isActive = key === currentScreen;

          return (
            <ToolbarButton
              key={key}
              appearance="subtle"
              icon={icon}
              className={mergeClasses(styles.menuButton, isActive ? styles.activeButton : false)}
              onClick={() => handleScreenChange(key)}
            >
              {tooltip}
            </ToolbarButton>
          );
        })}
      </PopoverSurface>
    </Popover>
  );
};
